/**
 * The card that sits ABOVE the phase stack. TWO SCOPES, stacked and visibly separated: which run
 * this is, and which CONNECTION settings governed it.
 *
 * `PhaseStage`'s three rows report what one phase measured — none of them names the run. Before this
 * card existed, `RunOverview`'s own `<h2>` carried that job, built from the run's own
 * `runStartedLabel(run)`. (Named rather than cited by line: that heading no longer exists in
 * `RunOverview.tsx` at all, so the `:92` this comment used to carry now points at unrelated JSX. It
 * WAS line 92 in the version this card replaced — the number did not rot by drifting, it rotted by
 * outliving its file.) This card REPLACES that heading, and the tie it enforced must survive the
 * move: `RunsPanel`'s row activator announces `Open run from ${runStartedLabel(run)}` (`RunsPanel.tsx`,
 * `activatesRowLabel`), and `aria-selected` on that row is spec-valid but not reliably announced (a
 * plain `role="table"` isn't conventionally a selection widget, and promoting it to `role="grid"` was
 * ruled out — it would change keyboard expectations for every table in the product). So the heading
 * below is built from the SAME `runStartedLabel(run)` call, not a second hand-written format that could
 * drift from the row's — that is what lets a screen-reader user land on this pane and match it back to
 * the row they opened, without `aria-selected` ever being read aloud.
 *
 * ── THE HEADING SHOWS A DATE AND ANNOUNCES A SENTENCE ───────────────────────
 * Figma `334:514` (`539:1001`) types the heading as the bare timestamp — `Aug 24 2026 at 9:12am` —
 * with no `Run from` prefix, because in a pane whose every other line is about this run the prefix is
 * three words of chrome. The tie above, however, is a match between two STRINGS, and shortening one
 * of them breaks it. So the prefix survives in an `aria-label`: the accessible name is still
 * `Run from ${runStartedLabel(run)}`, byte-for-byte what the row activator announces, while the visible
 * text is the frame's. Do not "simplify" that attribute away — the tests that pin the tie query by
 * accessible NAME, and they would keep passing right up until a screen-reader user needed them not to.
 *
 * `aria-label` rather than an `sr-only` span, and the difference is not cosmetic: an `sr-only` prefix
 * is a SEPARATE text node, and the accessible-name algorithm concatenates each node's own trimmed
 * text — so `Run from ` + the date computes as `Run fromAug 24 2026 at 9:12am`, which matches the row
 * activator's announcement nowhere. Measured, not assumed: it is what made the tie tests fail. The
 * visible text stays a strict substring of the label, so "label in name" still holds for voice control.
 *
 * ── THE THREE LINES ─────────────────────────────────────────────────────────
 * The frame splits what used to be one truncating meta string into two lines of two facts each:
 * `{state} · {duration}` then `{trigger} · {type}`. That is the same information the single line
 * carried, minus the run id, and it stops the pane's most diagnostic sentence truncating at 23rem —
 * the exact defect its own predecessor (`RunOverview`'s `ActionTooltip`-wrapped span) was removed for.
 * Neither line restates the timestamp the heading already states.
 *
 * The RUN ID is deliberately gone from the visible composition, and it is not lost: `useRunSelection`
 * keeps the open run's id in the URL as `?run=<id>`, which is where a reader copies it from and where
 * a link to this exact run already comes from. Nothing tested it and no copy affordance read it —
 * checked before removing it.
 *
 * The error string lives HERE, in the danger token, not inside `PhaseStage` or a status `Callout` (the
 * target has none). `break-words` is carried forward from `RunOverview`'s own note on it —
 * destination errors routinely carry a URL, id, or JSON fragment with no space in it, and this pane is
 * ~320px of content, so a long unbroken token needs somewhere to wrap.
 *
 * IT NAMES THE PHASE. "Send failed: task cancelled", not the old `Sync to "<destination>" failed with
 * error: "..."`. Two changes, and both remove a claim rather than adding one. The destination is gone
 * because the payload's error names no party — the only non-empty error across all 17 fixtures is
 * `task cancelled` — and blaming the far side is exactly the misattribution `run-phases.ts` warns
 * about, where a destination 400/401 lands in EXTRACT's invalid counts. The phase is named because
 * `failedPhase()` already derives it: it is the same derivation that paints the danger dot on the row
 * below, so the sentence and the dot are now one statement instead of two the reader must join. The
 * derivation can only yield Extract or Send; see `failedPhase`.
 *
 * WIDENED, UNOBSERVED — RESOLVED BY WORDING, not by a gate. The line still renders whenever
 * `run.error` is truthy, a RUNNING run included; no fixture carries a non-empty `error` on a running
 * run and nothing in the payload says whether the producer can write one mid-flight. The previous note
 * here said an `isRunning` gate would be a behaviour change resting on that same absent evidence, and
 * that is still true — so the fix is the sentence, not a branch. A run with an error but no failed
 * phase now reads `Sync error: "..."`, which asserts no failure and no phase, only that the payload
 * carried an error string. Nothing is suppressed and nothing is claimed.
 */
import { Card } from '@/_shared/components/ui/card';
import { cn } from '@/_shared/lib/utils';

import { failedPhase, PHASE_LABEL } from '../lib/run-phases';
import { formatDurationSeconds, isRunning, runDurationSeconds } from '../lib/run-view';
import { useTickingNow } from '../lib/use-ticking-now';
import type { IdentifierMapping, RawSyncRun } from '../types';
import { ConnectionSettingsBand } from './ConnectionSettingsBand';
import { runStartedLabel, runTriggerLabel, runTypeLabel } from './RunLabels';
import { SyncStatusBadge } from './SyncStatusBadge';

interface RunIdentityCardProps {
  run: RawSyncRun;
  /** `sync.config.destination.syncMode` — connection-scoped; see `ConnectionSettingsBand`. */
  syncMode?: string | undefined;
  /**
   * This connection's own retention, already resolved by `retentionDays(sync)`. REQUIRED, not
   * defaulted, and `null` (retention OFF) is a real value — the same contract `RunsPanel` and
   * `PastRunsRangePicker` state for the same number, for the same reason: `retentionDays(sync)` is the
   * ONE place a missing value may become the platform default.
   */
  retentionDays: number | null;
  /** `sync.config.destination.identifierMappings` — passed straight to the band; see its doc. */
  identifierMappings?: IdentifierMapping[] | undefined;
  /** `sync.config.destination.fieldMappings` — LinkedIn's or Gainsight PX's second table; see the band's doc. */
  fieldMappings?: IdentifierMapping[] | undefined;
  /** The second table's name for this destination; see the band's doc. */
  fieldMappingsLabel?: string | undefined;
  /** Opens the sync's edit sheet, from the connection band's `Edit`. */
  onEditConnection?: (() => void) | undefined;
  /** Why `Edit` on the connection band is unavailable. Threaded through — see the band's own prop. */
  editConnectionDisabledReason?: string | undefined;
  className?: string;
}

export function RunIdentityCard({
  run,
  syncMode,
  retentionDays,
  identifierMappings,
  fieldMappings,
  fieldMappingsLabel,
  onEditConnection,
  editConnectionDisabledReason,
  className,
}: RunIdentityCardProps): React.ReactElement {
  const running = isRunning(run);
  // Derived, not read from the payload — the same call that paints the danger dot on the row below.
  const failed = failedPhase(run);
  // Ticks only while the run is live; a finished run's duration comes from its own two timestamps —
  // same rule `RunOverview` follows for the same reason.
  const now = useTickingNow(running);
  const duration = formatDurationSeconds(runDurationSeconds(run, now));

  /**
   * `sync_type` is the REQUEST before the run's second phase and the DECISION after — it can change
   * from `incremental` to `Full` inside one run id (observed: the same run reports 'full' at +0.0s and
   * 'Full' at +0.2s, `liveExtract` → `liveSendingNothingDelivered` in `run-fixtures.ts`). A type shown
   * while a run is still running is therefore potentially a lie, and the payload carries no flag to say
   * which it is — only `status` and `state` do. `runTypeLabel` is rendered unconditionally below because
   * there is no honest rule to suppress it by: hiding it while `running` would ALSO hide it for the
   * common case where the request and the eventual decision agree, and there is nothing in the payload
   * that distinguishes "still provisional" from "already settled" while a run is in flight.
   */
  // `run.state` is typed optional but the wire sends an empty string, not `undefined` — guarded falsy
  // per convention for this field. `||` and `??` genuinely diverge here and the divergence is tested:
  // under `??` the slot collapses and the line reads `· 19s` with nothing in front of the separator.
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- guarding falsy on purpose, see above
  const state = run.state || '—';
  const outcome = `${state} · ${duration}`;
  const provenance = `${runTriggerLabel(run)} · ${runTypeLabel(run)}`;

  return (
    // `Card`, not a hand-rolled div: the four style classes this used to spell out
    // (`rounded-xl border border-border bg-card`) ARE the primitive's root, so the duplicate could
    // only ever drift away from it.
    //
    // `p-0`/`gap-0` — the two SECTIONS own their padding now, not the card. The connection band below
    // is tinted and runs edge to edge to its own bottom corners; a card padding would inset it into a
    // floating grey rectangle and destroy the one thing it is for (see `ConnectionSettingsBand`). `cn`
    // is `twMerge`, so these supersede the primitive's `py-6`/`gap-6` rather than fighting them.
    <Card className={cn('gap-0 p-0', className)}>
      {/* Run scope. 14/12 padding and a 4px rhythm — the frame's own figures (`539:999`), and the same
          14/12 the phase card beside it uses, so the two cards' contents start on one line. */}
      <div className="flex flex-col gap-1 px-3.5 py-3">
        <div className="flex items-center gap-2">
          {/* Built from `runStartedLabel(run)` — the same call `RunsPanel`'s row activator label uses —
              so the two strings cannot drift apart. The `sr-only` prefix is what keeps that tie exact
              while the visible text matches the frame; see this file's header. */}
          <h2
            className="min-w-0 flex-1 truncate text-[0.8125rem] leading-[1.125rem] font-semibold text-foreground"
            aria-label={`Run from ${runStartedLabel(run)}`}
          >
            {runStartedLabel(run)}
          </h2>
          <SyncStatusBadge run={run} className="shrink-0" />
        </div>

        {/* Two lines, not one truncating string — see this file's header. `title` on each so a
            narrow pane still surfaces the whole line natively rather than losing its tail. */}
        <p className="truncate text-xs text-muted-foreground" title={outcome}>
          {outcome}
        </p>
        <p className="truncate text-xs text-muted-foreground" title={provenance}>
          {provenance}
        </p>

        {run.error ? (
          <p className="text-xs break-words text-danger">
            {failed ? `${PHASE_LABEL[failed]} failed: ${run.error}` : `Sync error: ${run.error}`}
          </p>
        ) : null}
      </div>

      {/* Connection scope. Inside this card and visibly NOT inside the block above it — the tint, the
          hairline and the caps label are jointly what say "this one is not about the run you opened". */}
      <ConnectionSettingsBand
        syncMode={syncMode}
        retentionDays={retentionDays}
        identifierMappings={identifierMappings}
        fieldMappings={fieldMappings}
        fieldMappingsLabel={fieldMappingsLabel}
        onEdit={onEditConnection}
        editDisabledReason={editConnectionDisabledReason}
      />
    </Card>
  );
}
